/**
 * 会话累计 token 预算熔断（Circuit Breaker）。
 *
 * 目标：避免 AI 驱动 CLI 时 token 消耗失控（例如反复探查大库 schema、单次报表任务
 * 跨多命令累计输出爆炸）。机制是「跨命令累计」：CLI 在运行时状态目录下 `.baskreport/` 目录
 * 维护 `budget.json` 状态文件，每次命令执行后把本次 stdout 输出（及 --out 落盘内容）折算 token
 * 累加进文件，下一个命令启动先读文件，已超预算则硬阻断并以非零码退出。
 *
 * 设计要点：
 * - 状态文件按「运行时状态目录」落地（显式 --config-dir / BASK_CONFIG_DIR，缺省 cwd）；
 *   预算是「单次报表任务」的会话概念，跟随配置目录最直观——AI client 常以安装目录为 cwd
 *   启动，若按 cwd 落盘会把状态写到安装目录而非用户项目。
 *   与日志 `.baskreport/baskreport.log` 统一收敛到 `.baskreport/` 目录，根目录只留配置。
 *   开新任务请用 --budget-reset 清零，否则同目录下连续多任务会串预算（见 SKILL.md R8）。
 * - token 用字符近似折算（中文 1 字≈1 token、非中文 4 字符≈1 token），不引 tokenizer 依赖。
 *   必须用码点遍历（for...of + codePointAt），否则代理对（emoji、CJK 扩展 B+）会误判。
 * - fail-open：状态文件读写异常时降级为「不记账、不阻断」，避免熔断机制自身导致命令崩溃。
 * - 不会触达服务端：超预算检查在执行任何联网/生成逻辑之前完成。
 */

import { existsSync, mkdirSync, readFileSync, renameSync, writeFileSync } from 'node:fs';
import { dirname, resolve } from 'node:path';
import { getStateDir, readBudgetConfig } from './config.js';
import { getLogger } from './logger.js';

const log = getLogger('core:budget');

/** 默认单次报表任务预算（token）。覆盖绝大多数单报表任务留有余量。 */
export const DEFAULT_BUDGET_TOKENS = 500000;

/**
 * 单命令 stdout 输出折算 token 的前置告警阈值。
 * 跨过阈值时 stderr 提示一次，供 AI 在预算耗尽前主动收尾（非阻断，仅警示）。
 */
export const OUTPUT_WARN_THRESHOLD_TOKENS = 50000;

/** 运行时状态目录名（状态目录下，dotfile，项目级）。会被 .gitignore 忽略。 */
export const BUDGET_DIR_NAME = '.baskreport';

/** 状态文件名（位于 .baskreport/ 目录内）。 */
export const BUDGET_FILE_NAME = 'budget.json';

/** 状态文件内容结构。 */
export interface BudgetState {
  /** 已累计消耗的 token（跨命令累加）。 */
  usedTokens: number;
  /** 本次会话预算上限（token）。 */
  limitTokens: number;
  /** 累计成功执行的命令数（仅统计成功路径）。 */
  commandCount: number;
  /** 上次重置（开新会话）的 ISO 时间。 */
  lastReset: string;
}

/** 解析状态文件绝对路径（<状态目录>/.baskreport/budget.json；状态目录跟随 --config-dir / BASK_CONFIG_DIR，缺省 cwd）。 */
export function resolveBudgetFile(): string {
  return resolve(getStateDir(), BUDGET_DIR_NAME, BUDGET_FILE_NAME);
}

/**
 * 字符计数（码点遍历，代理对安全）。与 estimateTokens 共用同一判定逻辑，
 * 供 stdout 拦截器增量累计，避免每次 chunk 全量重算 O(n²)。
 */
export interface CharCount {
  /** CJK / 全角字符数（1 字 ≈ 1 token）。 */
  cjk: number;
  /** 其余字符数（每 4 个 ≈ 1 token）。 */
  other: number;
}

/** CJK / 全角字符判定（码点区间，与 estimateTokens 折算口径一致）。 */
function isCjkToken(code: number): boolean {
  return (
    (code >= 0x4e00 && code <= 0x9fff) || // CJK 统一表意
    (code >= 0x3400 && code <= 0x4dbf) || // 扩展 A
    (code >= 0xf900 && code <= 0xfaff) || // 兼容表意
    (code >= 0x3000 && code <= 0x303f) || // CJK 标点
    (code >= 0xff00 && code <= 0xffef) // 全角字符（含全角 ASCII）
  );
}

/** 统计文本中的 CJK/全角字符数与非 CJK 字符数（码点遍历）。 */
export function countChars(text: string): CharCount {
  const c: CharCount = { cjk: 0, other: 0 };
  for (const ch of text) {
    const code = ch.codePointAt(0) ?? 0;
    if (isCjkToken(code)) c.cjk += 1;
    else c.other += 1;
  }
  return c;
}

/** 由字符计数折算 token（与 estimateTokens 数学口径一致）。 */
export function tokensOfCounts(c: CharCount): number {
  return c.cjk + (c.other === 0 ? 0 : Math.ceil(c.other / 4));
}

/**
 * 字符近似折算 token（码点遍历）：
 * - 中文字符（CJK、CJK 扩展 A、兼容表意、CJK 标点、全角字符）按 1 token/字；
 * - 其余字符每 4 个折 1 token（向上取整保证至少 1，纯非中文非空文本最小 1）。
 * 对「防止雪崩」目标精度足够，且不依赖任何 tokenizer 依赖。
 */
export function estimateTokens(text: string): number {
  if (!text) return 0;
  return tokensOfCounts(countChars(text));
}

/**
 * 读取预算状态。不存在 / 损坏 / 目录不可访问时返回 null（调用方按「未启用」处理，fail-open）。
 */
export function readBudget(): BudgetState | null {
  try {
    const file = resolveBudgetFile();
    if (!existsSync(file)) return null;
    const raw = readFileSync(file, 'utf-8').replace(/^﻿/, '');
    const obj = JSON.parse(raw) as Partial<BudgetState>;
    if (typeof obj !== 'object' || obj === null) return null;
    const used = Number(obj.usedTokens);
    const limit = Number(obj.limitTokens);
    if (!Number.isFinite(used) || !Number.isFinite(limit)) return null;
    return {
      usedTokens: used,
      limitTokens: limit,
      commandCount: Number.isFinite(Number(obj.commandCount)) ? Number(obj.commandCount) : 0,
      lastReset: typeof obj.lastReset === 'string' ? obj.lastReset : new Date(0).toISOString(),
    };
  } catch {
    return null;
  }
}

/**
 * 解析预算上限：CLI --max-tokens > env BASK_MAX_TOKENS > config.budget.maxTokens > 默认 500000。
 * 任一来源非法时向下回退，最终回退到 DEFAULT_BUDGET_TOKENS。
 */
export function resolveBudgetLimit(): number {
  const fromCli = Number(process.env.BUDGET_LIMIT_CLI);
  if (Number.isFinite(fromCli) && fromCli > 0) return Math.floor(fromCli);

  const fromEnv = Number(process.env.BASK_MAX_TOKENS);
  if (Number.isFinite(fromEnv) && fromEnv > 0) return Math.floor(fromEnv);

  const fromConfig = readBudgetConfig().maxTokens;
  if (typeof fromConfig === 'number' && fromConfig > 0) return Math.floor(fromConfig);

  return DEFAULT_BUDGET_TOKENS;
}

/** 软写状态文件（原子：temp 写入后 rename；自动创建 .baskreport/ 目录）。失败静默 fail-open。 */
function writeStateAtomic(state: BudgetState): void {
  const file = resolveBudgetFile();
  mkdirSync(dirname(file), { recursive: true });
  const tmp = `${file}.${process.pid}.tmp`;
  writeFileSync(tmp, JSON.stringify(state, null, 2), 'utf-8');
  renameSync(tmp, file);
}

/**
 * 累计本次消耗并写回；fail-open 不抛错。仅成功路径调用。
 * @param used 本次命令消耗的 token
 * @param limit 当前会话预算上限
 */
export function chargeBudget(used: number, limit: number): void {
  try {
    const prev = readBudget();
    const next: BudgetState = prev
      ? { ...prev, usedTokens: prev.usedTokens + used, commandCount: prev.commandCount + 1 }
      : {
          usedTokens: used,
          limitTokens: limit,
          commandCount: 1,
          lastReset: new Date().toISOString(),
        };
    writeStateAtomic(next);
    printProgress(next);
  } catch {
    // fail-open：记账失败不影响命令结果
  }
}

/** 打印一行 stderr 轻量进度，供 AI 在耗尽前主动收尾（非静默）。 */
function printProgress(state: BudgetState): void {
  try {
    log.info(`[budget] 已用 ${state.usedTokens}/${state.limitTokens} tokens，命令数 ${state.commandCount}`);
  } catch {
    /* 忽略 */
  }
}

/**
 * 清零状态文件开启新会话；limit 沿用当前解析上限，不清空。
 * 打印一句 stderr 确认。
 */
export function resetBudget(limit: number): void {
  try {
    const state: BudgetState = {
      usedTokens: 0,
      limitTokens: limit,
      commandCount: 0,
      lastReset: new Date().toISOString(),
    };
    writeStateAtomic(state);
    log.info(`[budget] 预算已重置：上限 ${limit} tokens`);
  } catch {
    // 即使重置失败也尽量提示
    try {
      log.warn('[budget] 预算重置失败（状态文件不可写），继续无熔断执行');
    } catch {
      /* 忽略 */
    }
  }
}

/** 落盘文本记账器（通道 B）：action 在 writeFileSync 时调用 chargeText(content)。 */
export interface BudgetCharger {
  chargeText(text: string): void;
}

/**
 * 返回落盘文本记账器（通道 B）。收集到的文本会在 withBudget 成功路径末尾一并折算入账。
 * 仅在 withBudget 包裹的 action 执行期间有效。
 */
export function getBudgetCharger(): BudgetCharger {
  chargeAccumulator = chargeAccumulator ?? { text: '' };
  return {
    chargeText(text: string): void {
      if (chargeAccumulator) chargeAccumulator.text += text ?? '';
    },
  };
}

/** 模块内用于暂存 --out 落盘文本的累加器（单次命令生命周期）。 */
let chargeAccumulator: { text: string } | null = null;

/**
 * 包裹子命令 action：先检查熔断（早于 applyConfigDir/联网），成功执行后记账。
 * 原样透传 Commander 的 (...args) 给原始 action。
 *
 * 行为：
 * - 若全局 --budget-reset：清零状态文件并退出（开新会话）。
 * - 若 --no-budget：跳过一切熔断检查与记账，直接执行。
 * - 否则读状态，已超预算则打印 STOP 文案并以 exit(2) 硬阻断；否则执行，成功后记账
 *   （stdout 拦截文本 + 落盘文本）。
 *
 * 注意：fail-open —— 状态文件读写异常或 limit 非法时视为「不熔断、不记账」。
 */
export function withBudget(
  action: (...args: any[]) => Promise<void> | void,
): (...args: any[]) => Promise<void> {
  return async (...args: any[]): Promise<void> => {
    const opts = (args.find((a) => a && typeof a === 'object' && 'maxTokens' in a) as any) ?? {};
    const noBudget = opts.budget === false || opts.noBudget === true;
    const doReset = opts.budgetReset === true;

    if (doReset) {
      resetBudget(resolveBudgetLimit());
      process.exit(0);
      return;
    }

    if (noBudget) {
      await action(...args);
      return;
    }

    const limit = resolveBudgetLimit();
    const state = readBudget();

    if (state && state.usedTokens >= state.limitTokens) {
      log.error(
        `\n[预算耗尽 STOP] 已用 ${state.usedTokens}/${state.limitTokens} tokens，` +
          `命中熔断。请停止并报导用户：本次报表任务的 token 预算已耗尽，不要再继续调用 CLI。`,
      );
      process.exit(2);
      return;
    }

    // 安装 stdout 拦截器（通道 A）
    const originalWrite = process.stdout.write.bind(process.stdout);
    let captured = '';
    // 增量字符计数（每次 chunk 累加 O(chunk)，避免每次全量重算 O(n²)）
    const outCounts: CharCount = { cjk: 0, other: 0 };
    let outputWarned = false;
    const interceptor = (chunk: any, ...rest: any[]): boolean => {
      const s = typeof chunk === 'string' ? chunk : Buffer.isBuffer(chunk) ? chunk.toString('utf-8') : null;
      if (s !== null) {
        captured += s;
        const c = countChars(s);
        outCounts.cjk += c.cjk;
        outCounts.other += c.other;
      }
      // 单命令输出体积前置告警（非阻断）：折算 token 首次跨过阈值时 stderr 提示一次，
      // 供 AI 在预算耗尽前主动收尾（如改精简视图 / 落盘后分段读）。
      if (!outputWarned && tokensOfCounts(outCounts) >= OUTPUT_WARN_THRESHOLD_TOKENS) {
        outputWarned = true;
        try {
          log.warn(
            `\n[输出过大警示] 本次命令 stdout 已约 ${tokensOfCounts(outCounts)} tokens，` +
              `继续输出将快速耗尽会话预算。建议改用精简视图（如 report datasets）` +
              `或将输出落盘（--out file）后先查体积再分段读取。`,
          );
        } catch {
          /* 忽略 */
        }
      }
      return (originalWrite as any)(chunk, ...rest);
    };
    (process.stdout.write as any) = interceptor;

    chargeAccumulator = { text: '' };

    try {
      await action(...args);
      // 成功路径才记账：若 action 自行设了非零 exitCode（业务失败/校验未过），
      // 视为失败不计入，避免把「报错刷屏」当消耗（计划：失败命令不记账）。
      if (!process.exitCode) {
        const total = estimateTokens(captured) + estimateTokens(chargeAccumulator?.text ?? '');
        chargeBudget(total, limit);
      }
    } finally {
      // 恢复 stdout 拦截器，清理累加器
      (process.stdout.write as any) = originalWrite;
      chargeAccumulator = null;
    }
  };
}
